import { supabase } from '@/lib/supabase';

export interface UploadResult {
  url: string;
  path: string;
}

export interface CourseImageUploadOptions {
  courseId: string;
  file: File;
  type: 'cover' | 'thumbnail' | 'badge';
}

/**
 * Faz upload de uma imagem para o bucket courses do Supabase
 * Organiza as imagens em pastas por ID do curso
 */
export const uploadCourseImage = async ({
  courseId,
  file,
  type
}: CourseImageUploadOptions): Promise<UploadResult> => {
  try {
    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      throw new Error('Arquivo deve ser uma imagem');
    }

    // Validar tamanho do arquivo (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      throw new Error('Arquivo deve ter no máximo 5MB');
    }

    // Obter extensão do arquivo
    const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'png';
    
    // Criar nome do arquivo com prefixo baseado no tipo
    const fileName = `${type}_${courseId}.${fileExtension}`;
    
    // Criar caminho da pasta (courses/{courseId}/{fileName})
    const filePath = `${courseId}/${fileName}`;

    // Fazer upload do arquivo
    const { data, error } = await supabase.storage
      .from('courses')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Sobrescrever se já existe
      });

    if (error) {
      throw error;
    }

    // Obter URL pública do arquivo
    const { data: urlData } = supabase.storage
      .from('courses')
      .getPublicUrl(filePath);

    return {
      url: urlData.publicUrl,
      path: filePath
    };
  } catch (error) {
    console.error('Erro no upload:', error);
    throw error;
  }
};

/**
 * Remove uma imagem do bucket courses
 */
export const removeCourseImage = async (filePath: string): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from('courses')
      .remove([filePath]);

    if (error) {
      throw error;
    }
  } catch (error) {
    console.error('Erro ao remover imagem:', error);
    throw error;
  }
};

/**
 * Lista todas as imagens de um curso
 */
export const listCourseImages = async (courseId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase.storage
      .from('courses')
      .list(courseId);

    if (error) {
      throw error;
    }

    return data?.map(file => `${courseId}/${file.name}`) || [];
  } catch (error) {
    console.error('Erro ao listar imagens:', error);
    throw error;
  }
};

/**
 * Valida se um arquivo é uma imagem válida
 */
export const validateImageFile = (file: File): string | null => {
  // Verificar tipo
  if (!file.type.startsWith('image/')) {
    return 'Arquivo deve ser uma imagem';
  }

  // Verificar tamanho (5MB max)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    return 'Arquivo deve ter no máximo 5MB';
  }

  // Verificar extensões permitidas
  const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!extension || !allowedExtensions.includes(extension)) {
    return 'Formato não suportado. Use: JPG, PNG, GIF ou WebP';
  }

  return null; // Válido
};

/**
 * Gera preview de uma imagem antes do upload
 */
export const generateImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Erro ao gerar preview'));
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
    reader.readAsDataURL(file);
  });
};
